import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { BarChart3, Eye, EyeOff, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

export default function ResetPasswordPage() {
  const [, setLocation] = useLocation();
  const { t } = useTranslation(["auth", "common"]);
  const { toast } = useToast();
  const [form, setForm] = useState({ newPassword: "", confirmPassword: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [tokenStatus, setTokenStatus] = useState<"checking" | "valid" | "invalid">("checking");
  const [invalidReason, setInvalidReason] = useState("");
  const [done, setDone] = useState(false);

  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  useEffect(() => {
    if (!token) {
      setTokenStatus("invalid");
      setInvalidReason(t("auth:resetPassword.invalidNoToken"));
      return;
    }
    fetch(`/api/auth/reset-password/validate?token=${encodeURIComponent(token)}`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data.valid) {
          setTokenStatus("valid");
        } else {
          setTokenStatus("invalid");
          setInvalidReason(data.message ?? t("auth:resetPassword.invalidDefault"));
        }
      })
      .catch(() => {
        setTokenStatus("invalid");
        setInvalidReason(t("auth:resetPassword.invalidValidateError"));
      });
  }, [token]);

  const resetMutation = useMutation({
    mutationFn: (data: { token: string; newPassword: string }) =>
      apiRequest("POST", "/api/auth/reset-password", data),
    onSuccess: () => {
      setDone(true);
    },
    onError: (err: any) => {
      toast({ title: t("auth:resetPassword.toast.resetFailed"), description: err.message ?? t("common:tryAgain"), variant: "destructive" });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.newPassword.length < 8) {
      toast({ title: t("auth:resetPassword.toast.passwordTooShort"), description: t("auth:resetPassword.toast.passwordMinChars"), variant: "destructive" });
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      toast({ title: t("auth:resetPassword.toast.passwordsDontMatch"), description: t("auth:resetPassword.toast.passwordsSameMsg"), variant: "destructive" });
      return;
    }
    resetMutation.mutate({ token, newPassword: form.newPassword });
  };

  return (
    <div className="min-h-screen bg-background flex">
      <div className="hidden lg:flex lg:w-1/2 bg-primary flex-col justify-between p-12">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-foreground/10 flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-primary-foreground">DealerMind</h1>
            <p className="text-primary-foreground/70 text-xs">{t("auth:brand.tagline")}</p>
          </div>
        </div>
        <p className="text-primary-foreground/40 text-xs">{t("auth:brand.copyright")}</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-primary-foreground" />
            </div>
            <h1 className="text-xl font-bold">DealerMind</h1>
          </div>

          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl">
                {done ? t("auth:resetPassword.titleDone") : t("auth:resetPassword.titleSet")}
              </CardTitle>
              <CardDescription>
                {done ? t("auth:resetPassword.descDone") : t("auth:resetPassword.descSet")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {tokenStatus === "checking" && (
                <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">{t("auth:resetPassword.checking")}</span>
                </div>
              )}

              {tokenStatus === "invalid" && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                    <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                    <p className="text-sm text-destructive">{invalidReason}</p>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setLocation("/login")}
                    data-testid="button-back-to-login"
                  >
                    {t("auth:resetPassword.backToLogin")}
                  </Button>
                </div>
              )}

              {tokenStatus === "valid" && done && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-chart-3/10 border border-chart-3/20">
                    <CheckCircle2 className="w-5 h-5 text-chart-3 shrink-0 mt-0.5" />
                    <p className="text-sm text-chart-3 font-medium">{t("auth:resetPassword.successMsg")}</p>
                  </div>
                  <Button
                    className="w-full"
                    onClick={() => setLocation("/login")}
                    data-testid="button-go-to-login"
                  >
                    {t("auth:resetPassword.signIn")}
                  </Button>
                </div>
              )}

              {tokenStatus === "valid" && !done && (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="newPassword">{t("auth:resetPassword.newPassword")}</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showPassword ? "text" : "password"}
                        placeholder={t("auth:resetPassword.newPasswordPlaceholder")}
                        value={form.newPassword}
                        onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                        data-testid="input-new-password"
                        autoComplete="new-password"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                        onClick={() => setShowPassword(!showPassword)}
                        data-testid="button-toggle-new-password"
                      >
                        {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="confirmPassword">{t("auth:resetPassword.confirmNewPassword")}</Label>
                    <Input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder={t("auth:resetPassword.confirmPlaceholder")}
                      value={form.confirmPassword}
                      onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                      data-testid="input-confirm-password"
                      autoComplete="new-password"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={resetMutation.isPending}
                    data-testid="button-submit-reset"
                  >
                    {resetMutation.isPending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("auth:resetPassword.updating")}</>
                    ) : t("auth:resetPassword.setNewPassword")}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    <button
                      type="button"
                      className="underline-offset-4 hover:underline"
                      onClick={() => setLocation("/login")}
                      data-testid="button-back-to-login"
                    >
                      {t("auth:resetPassword.backToLogin")}
                    </button>
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
